<template>
  <div
    v-if="offer"
    class="add-to-cart-prompt-container"
  >
    <div class="add-to-cart-prompt-container__default">
      <AddToCartButtonContainer
        v-if="isAddToCartAllowed"
        :offer="offer"
        theme="primary"
        :display-cart-quantity="false"
        :display-icon="false"
        class="add-to-cart-prompt-container__button add-to-cart-prompt-container__button--buy"
      />
      <div
        v-else-if="magentaMomentsOffer"
        class="add-to-cart-prompt-container__moments"
        v-html="magentaMomentsOffer.addToCartRejectionNote"
      />
      <client-only>
        <LinkButton
          v-if="isBookmarkable"
          class="add-to-cart-prompt-container__button add-to-cart-prompt-container__button--bookmark"
          :class="{ 'add-to-cart-prompt-container__button--bookmark--active': isBookmarked }"
          tag="button"
          :outline="!isBookmarked"
          :outline-primary="isBookmarked"
          icon-only
          :aria-label="
            !isBookmarked
              ? 'Produkt zur Merkliste hinzufügen'
              : 'Produkt von der Merkliste entfernen'
          "
          @click="toggleBookmarkNumber"
        >
          <svg-icon
            no-icon-class
            :image="IconBookmark"
          />
        </LinkButton>
      </client-only>
    </div>
    <client-only>
      <div
        v-if="paypalExpressIsVisible && offer?.product?.articleNumber"
        class="add-to-cart-prompt-container__express"
      >
        <div class="add-to-cart-prompt-container__divider">
          <span>Express Checkout</span>
        </div>

        <!-- fe decides, to show ppe or not -->
        <PaypalExpressCheckoutButton
          class="add-to-cart-prompt-container__button add-to-cart-prompt-container__button--paypal"
          :offer="offer"
          size="normal"
        />
      </div>
    </client-only>
  </div>
</template>

<script lang="ts" setup>
import { type PropType, computed } from 'vue';
import { useNuxtApp, useRuntimeConfig } from '#imports';
import { getPrice } from '@i22/rocket';
import { getPricesFromOfferWithRebate } from '@/lib/price-from-offer';
import AddToCartButtonContainer from '@/components/product-detail/add-to-cart-button-container.vue';
import { LinkButton } from '@i22/td-longtail-vue-components';
import PaypalExpressCheckoutButton from '@/components/paypal-express-checkout-button.vue';
import IconBookmark from '@/assets/images/icon-bookmark.svg';
import useProductBookmarks from '@/composables/product-bookmarks';
import type { Rebate } from '@/store/offers';
import { getMagentaMomentOffer, isAllowedToAddToCart } from '~/lib/magenta-moments';
import { isManualBundle } from '~/lib/build-bundle-from-products';
import type { Offer } from '~/lib/goliath/offer-by-slugs';

const expressMinimumAmount = 1;

const { isProductInBookmarks, isBookmarkingAvailable, toggleBookmark } = useProductBookmarks();

const { $store: store, $route: route } = useNuxtApp();
const config = useRuntimeConfig();

const props = defineProps({
  offer: {
    type: Object as PropType<Offer>,
    default: () => undefined,
  },
});

const rebate = computed<Rebate>(
  () => store.getters['offers/rebates'][props.offer?.product?.articleNumber || '']
);

const price = computed<number | undefined>(() => {
  if (!props.offer) return undefined;
  const prices = getPricesFromOfferWithRebate(props.offer, rebate.value);
  if (!prices) return undefined;
  return getPrice(prices as any, 'DEFAULT', 'ONETIME')?.value;
});

const showExpressButtons = computed(() => {
  if (!store.getters['cart/cartIsEmpty']) return false;
  return (
    typeof price.value === 'number' &&
    price.value >= expressMinimumAmount &&
    (props.offer?.product?.availability?.stock || 0) > 0
  );
});

const paypalExpressIsVisible = computed(() => {
  if (config.public.paypalExpressDetailPage.disabled) return false;
  if (!store.getters['cart/cartInitialized']) return false;
  if (store.getters['cart/wasPaypalExpressReceived']) return false;
  return showExpressButtons.value;
});

const isBookmarked = computed(() => {
  if (!props.offer?.product?.articleNumber) return false;
  return isProductInBookmarks(props.offer.product.articleNumber);
});

const magentaMomentsOffer = computed(() =>
  getMagentaMomentOffer(props.offer?.product?.articleNumber || undefined)
);

const isBookmarkable = computed(
  () =>
    isBookmarkingAvailable &&
    !magentaMomentsOffer.value &&
    !isManualBundle(props.offer?.product?.articleNumber)
);

const isAddToCartAllowed = computed(() =>
  isAllowedToAddToCart(props.offer?.product?.articleNumber, route?.path)
);

const toggleBookmarkNumber = (e) => {
  e.preventDefault();
  if (!props.offer?.product?.articleNumber) return;
  toggleBookmark(props.offer.product.articleNumber, props.offer.product.name || '', price.value);
};

defineExpose({ paypalExpressIsVisible });
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.add-to-cart-prompt-container {
  display: flex;
  flex-direction: column;
  grid-gap: $grid-base;
  margin-bottom: $grid-base;

  @include mq($mq-md) {
    grid-gap: $grid-base * 2;
    margin-bottom: $grid-base * 2;
  }

  &__default,
  &__express {
    display: flex;
    grid-gap: $grid-base;

    @include mq($mq-md) {
      grid-gap: $grid-base * 2;
    }
  }

  &__express {
    border-bottom: 1px solid var(--color-stroke-subtle);
    padding-bottom: $grid-base * 2;
    flex-wrap: wrap;
  }

  &__divider {
    position: relative;
    flex: 0 0 100%;
    color: var(--color-font-subtle);
    text-align: center;
    font-weight: 700;
    margin-bottom: 1px;
    @include mq($mq-md) {
      margin-bottom: -11px;
    }

    &:after {
      position: absolute;
      content: '';
      width: 100%;
      border-bottom: 1px solid var(--color-stroke-subtle);
      display: block;
      margin-block: -1 * $grid-base;
      z-index: -1;
    }

    span {
      padding-inline: $grid-base;
      background: var(--color-bg);
    }
  }

  &__button {
    &--buy {
      font-weight: 700;
      width: 210px;
      @include mq(false, $mq-sm - 1) {
        width: 100%;
      }
    }

    &--bookmark {
      flex: 0 0 auto;
      > svg {
        fill: none;
        stroke: currentColor;
      }

      &--active {
        color: var(--color-primary);
        background-color: white;
        border-color: currentColor;

        > svg {
          fill: currentColor;
        }
      }
    }
  }

  &__moments {
    background: rgb(211, 215, 249);
    padding: 1.25rem;
    border-radius: 1rem;
    margin-right: $grid-base;
  }
}
</style>
